import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext.jsx';
import api from '../../services/api.js';
import Button from '../../components/common/Button.jsx';
import Logo from '../../components/common/Logo.jsx';

const PLAN_COLORS  = { FREE: '#6B7280', STARTER: '#3B82F6', PRO: '#8B5CF6', BUSINESS: '#F59E0B' };
const PLAN_POPULAR = 'PRO';

export default function BillingPage() {
  const { user, setUser }     = useAuth();
  const [plans, setPlans]     = useState([]);
  const [sub, setSub]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying]   = useState(null);
  const [searchParams]        = useSearchParams();

  useEffect(() => {
    Promise.all([
      api.get('/payments/plans'),
      api.get('/payments/subscription').catch(() => ({ data: { data: null } })),
    ]).then(([p, s]) => {
      setPlans(p.data.data.plans);
      setSub(s.data.data);
    }).finally(() => setLoading(false));

    if (searchParams.get('status') === 'success') {
      toast.success('Paiement confirmé ! Votre plan a été mis à jour.');
    }
  }, []);

  const handleSubscribe = async (planId) => {
    if (planId === 'FREE' || planId === 'BUSINESS') return;
    setPaying(planId);
    try {
      const res = await api.post('/payments/initiate', {
        plan:      planId,
        returnUrl: `${window.location.origin}/dashboard/billing?status=success`,
      });
      window.location.href = res.data.data.paymentUrl;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors du paiement');
      setPaying(null);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Annuler votre abonnement à la fin de la période ?')) return;
    await api.post('/payments/cancel');
    setSub(s => ({ ...s, cancelAtPeriodEnd: true }));
    toast.success('Abonnement annulé à la fin de la période');
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <div className="spinner" style={{ width: 40, height: 40 }} />
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>Plans & Abonnements</h1>
        <p style={{ color: 'var(--elvyra-text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4 }}>
          Plan actuel : <span style={{ color: PLAN_COLORS[user?.plan], fontWeight: 600 }}>{user?.plan}</span>
          {sub?.cancelAtPeriodEnd && <span style={{ color: 'var(--elvyra-warning)', marginLeft: 8 }}>(annulation programmée)</span>}
        </p>
      </div>

      {/* Grille des plans */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 40 }}>
        {plans.map(plan => {
          const isCurrent = user?.plan === plan.id;
          const isPopular = plan.id === PLAN_POPULAR;
          const color = PLAN_COLORS[plan.id];

          return (
            <div key={plan.id} className="card" style={{
              position: 'relative',
              borderColor: isCurrent ? color : isPopular ? `${color}44` : 'var(--elvyra-border)',
              borderWidth: isCurrent || isPopular ? 2 : 1,
            }}>
              {isPopular && !isCurrent && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: color, color: '#fff', fontSize: 10, fontWeight: 700,
                  padding: '3px 12px', borderRadius: 20,
                }}>
                  LE PLUS POPULAIRE
                </div>
              )}
              {isCurrent && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: color, color: '#fff', fontSize: 10, fontWeight: 700,
                  padding: '3px 12px', borderRadius: 20,
                }}>
                  ✓ PLAN ACTUEL
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <span style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color, letterSpacing: 2, textTransform: 'uppercase' }}>{plan.label}</span>
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  {plan.price === null ? (
                    <span style={{ fontSize: 'var(--text-xl)', fontWeight: 700 }}>Sur devis</span>
                  ) : (
                    <>
                      <span style={{ fontSize: 'var(--text-3xl)', fontWeight: 700 }}>{plan.price.toLocaleString('fr-FR')}</span>
                      <span style={{ color: 'var(--elvyra-text-muted)', fontSize: 'var(--text-sm)' }}>FCFA/mois</span>
                    </>
                  )}
                </div>
              </div>

              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-secondary)', marginBottom: 16 }}>
                <div>📹 {plan.cameras} caméra(s)</div>
                <div style={{ marginTop: 4 }}>🗄 Stockage {plan.storage}</div>
              </div>

              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
                {plan.features.map(f => (
                  <li key={f} style={{ fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-secondary)', display: 'flex', gap: 6 }}>
                    <span style={{ color }}>✓</span> {f}
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                sub && !sub.cancelAtPeriodEnd && plan.id !== 'FREE' ? (
                  <Button variant="secondary" size="sm" style={{ width: '100%' }} onClick={handleCancel}>
                    Annuler l'abonnement
                  </Button>
                ) : (
                  <div style={{ textAlign: 'center', fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)', padding: '8px 0' }}>Plan actif</div>
                )
              ) : plan.id === 'FREE' ? (
                <div style={{ textAlign: 'center', fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)', padding: '8px 0' }}>Gratuit</div>
              ) : plan.id === 'BUSINESS' ? (
                <Button variant="ghost" size="sm" style={{ width: '100%' }}>Nous contacter</Button>
              ) : (
                <Button
                  size="sm"
                  style={{ width: '100%', background: `linear-gradient(135deg, ${color}, ${color}CC)`, color: '#fff' }}
                  loading={paying === plan.id}
                  onClick={() => handleSubscribe(plan.id)}>
                  Souscrire
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Détail abonnement actif */}
      {sub && (
        <div className="card" style={{ maxWidth: 480 }}>
          <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: 12 }}>Détail abonnement</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 'var(--text-sm)' }}>
            <Row label="Plan"      value={sub.plan} />
            <Row label="Statut"    value={sub.status} />
            <Row label="Fournisseur" value={sub.provider} />
            <Row label="Renouvellement" value={new Date(sub.currentPeriodEnd).toLocaleDateString('fr-FR')} />
            {sub.cancelAtPeriodEnd && <Row label="Fin le" value={new Date(sub.currentPeriodEnd).toLocaleDateString('fr-FR')} color="var(--elvyra-warning)" />}
          </div>
        </div>
      )}

      <p style={{ marginTop: 32, fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)', textAlign: 'center' }}>
        Paiements sécurisés par CinetPay · DEKANI SOFTWARE · ELVYRA v1
      </p>
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--elvyra-text-secondary)' }}>{label}</span>
      <span style={{ fontWeight: 500, color: color || 'var(--elvyra-text-primary)' }}>{value}</span>
    </div>
  );
}
